import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { membershipSchema } from "../event-experience/schemas";
import { trackSwitchRequestSchema, type TrackSwitchRequest } from "./schemas";

const requestsPath = (eventId: string) =>
  ["events", eventId, "track_switch_requests"] as const;

export type OwnRequestState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "ready"; request: TrackSwitchRequest | null };

/**
 * The attendee's own request, live, so a decision shows up while they look.
 *
 * Rules: `get` when the caller's membership names this attendeeId. Not read
 * at all while `enabled` is false (the feature is off, or the member has no
 * roster row to request for).
 */
export function useOwnTrackSwitchRequest(
  eventId: string,
  attendeeId: string | undefined,
  enabled: boolean,
): OwnRequestState {
  const [state, setState] = useState<OwnRequestState>({ status: "loading" });

  useEffect(() => {
    if (!enabled || !attendeeId) {
      setState({ status: "ready", request: null });
      return;
    }
    setState({ status: "loading" });
    return onSnapshot(
      doc(db, ...requestsPath(eventId), attendeeId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setState({ status: "ready", request: null });
          return;
        }
        const result = trackSwitchRequestSchema.safeParse({
          id: snapshot.id,
          ...snapshot.data(),
        });
        setState({
          status: "ready",
          request: result.success ? result.data : null,
        });
      },
      (error) =>
        setState({
          status: "error",
          error: error instanceof Error ? error.message : "Failed to load",
        }),
    );
  }, [eventId, attendeeId, enabled]);

  return state;
}

export interface TrackSwitchWrites {
  saving: boolean;
  error: string | null;
  submit: (input: {
    toTrackId: string;
    fromTrackId: string | null;
    reason: string;
  }) => Promise<boolean>;
  withdraw: () => Promise<boolean>;
}

/**
 * Creating, changing and withdrawing the attendee's own request.
 *
 * A request is always written whole, which rules require: a change to an open
 * request and a new request after a decided one are the same write.
 */
export function useTrackSwitchWrites(
  eventId: string,
  uid: string,
  attendeeId: string | undefined,
): TrackSwitchWrites {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback<TrackSwitchWrites["submit"]>(
    async ({ toTrackId, fromTrackId, reason }) => {
      if (!attendeeId) return false;
      setSaving(true);
      setError(null);
      try {
        const trimmed = reason.trim();
        await setDoc(doc(db, ...requestsPath(eventId), attendeeId), {
          attendeeId,
          uid,
          fromTrackId,
          toTrackId,
          ...(trimmed ? { reason: trimmed } : {}),
          status: "OPEN",
          requestedAt: serverTimestamp(),
        });
        return true;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Could not send your request",
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [eventId, uid, attendeeId],
  );

  const withdraw = useCallback(async () => {
    if (!attendeeId) return false;
    setSaving(true);
    setError(null);
    try {
      await deleteDoc(doc(db, ...requestsPath(eventId), attendeeId));
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not withdraw your request",
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [eventId, attendeeId]);

  return { saving, error, submit, withdraw };
}

/**
 * The member's own track, live, while the feature is on.
 *
 * The page otherwise reads the membership once, and an accepted request moves
 * the attendee while they have the page open. Undefined until the first
 * snapshot, so the caller keeps the value it already has.
 */
export function useLiveMemberTrack(
  eventId: string,
  uid: string,
  enabled: boolean,
): { loaded: boolean; trackId?: string } {
  const [state, setState] = useState<{ loaded: boolean; trackId?: string }>({
    loaded: false,
  });

  useEffect(() => {
    setState({ loaded: false });
    if (!enabled) return;
    return onSnapshot(
      doc(db, "events", eventId, "members", uid),
      (snapshot) => {
        const result = membershipSchema.safeParse(snapshot.data() ?? {});
        setState({
          loaded: true,
          trackId: result.success ? result.data.trackId : undefined,
        });
      },
      // The page falls back to the membership it already read.
      () => setState({ loaded: false }),
    );
  }, [eventId, uid, enabled]);

  return state;
}

export type RequestListState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: TrackSwitchRequest[] };

/**
 * Every request in the event, live, for the organiser's Requests tab and its
 * badge. Rules: `list` for event admins only. No ordering in the query, so it
 * needs no index; the tab sorts.
 */
export function useTrackSwitchRequests(
  eventId: string,
  enabled: boolean,
): RequestListState {
  const [state, setState] = useState<RequestListState>({ status: "loading" });

  useEffect(() => {
    if (!enabled) {
      setState({ status: "success", data: [] });
      return;
    }
    setState({ status: "loading" });
    return onSnapshot(
      collection(db, ...requestsPath(eventId)),
      (snapshot) => {
        const data: TrackSwitchRequest[] = [];
        snapshot.forEach((item) => {
          const result = trackSwitchRequestSchema.safeParse({
            id: item.id,
            ...item.data(),
          });
          if (result.success) data.push(result.data);
        });
        setState({ status: "success", data });
      },
      (error) =>
        setState({
          status: "error",
          error: error instanceof Error ? error.message : "Failed to load",
        }),
    );
  }, [eventId, enabled]);

  return state;
}
